import Image from 'next/image'
import { cn } from '@/lib/utils'
import type { CosmicBelowFoldProductDescriptionType } from '@/lib/cms/cosmic/types/CosmicBelowFoldType'
import styles from './ProductDescription.module.css'

type Props = {
  productDescription?: CosmicBelowFoldProductDescriptionType
}

export const ProductDescriptionSection = ({ productDescription }: Props) => {
  if (!productDescription || productDescription.hide) return null

  return (
    <>
      <div
        style={{ backgroundColor: productDescription.backgroundColor }}
        className={`${styles.ellipse} relative z-10 h-40 md:h-52`}
        data-test="product-description-section"
      />
      <div
        style={{ backgroundColor: productDescription.backgroundColor }}
        className="relative z-20 flex w-full flex-col px-8 pt-20 pb-5 md:w-full md:flex-row md:gap-16 md:px-24 md:pt-24 md:pb-24"
      >
        <div className="flex h-1/2 w-full flex-col items-center justify-center md:h-full md:w-1/2 md:items-center">
          <span className="flex w-full flex-col items-center md:max-w-xl">
            <h2
              className={cn(
                'text-xl leading-tight font-semibold break-normal wrap-anywhere max-lg:text-4xl md:w-full md:text-4xl',
                productDescription.titleTextColor
                  ? `text-[${productDescription.titleTextColor}]`
                  : 'text-primary',
              )}
              data-test="product-description-section-title"
            >
              {productDescription.title}
            </h2>

            {productDescription.content && (
              <div
                dangerouslySetInnerHTML={{ __html: productDescription.content }}
                className="mt-7 text-[15px] leading-9 whitespace-pre-line text-primary md:w-full md:text-base"
                data-test="product-description-section-text"
              />
            )}
          </span>
        </div>

        <div className="mt-12 mb-20 flex h-1/2 min-h-0 w-full flex-col items-center justify-center md:my-0 md:h-full md:w-1/2 md:items-center">
          {/* Give precedence to video if one exists over image */}
          {productDescription?.video ? (
            // Disabling this, we do not have the captions for the video
            // eslint-disable-next-line jsx-a11y/media-has-caption
            <video controls>
              <source src={productDescription.video} type="video/mp4" />
            </video>
          ) : (
            <Image
              src={productDescription.image || 'We need a fallback image'}
              alt={productDescription.altText || ''}
              objectFit="cover"
              objectPosition="top"
              className="!relative rounded-md"
              height={400}
              width={600}
              data-test="product-description-section-image"
            />
          )}
        </div>
      </div>
    </>
  )
}
